import React, { useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaRegCopy, FaCheckCircle, FaArrowLeft } from "react-icons/fa";
import SEO from "./SEO";

const BANK_DETAILS = {
  bankName: "",
  companyName: "",
  accountNo: "",
  ifsc: "",
  branchAddress: "",
  accountType: "",
  upiId: "",
};

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL || "";

interface Loan {
  id: string;
  formattedLoanId: string;
  status: string;
  amount: number;
  purpose: string;
  loanType: string;
  applicationDate: string;
  approvalDate: string;
  disbursementDate: string;
  closureDate: string | null;
  createdAt: string;
  updatedAt: string;
}

interface RepaymentDetails {
  principalAmount: number;
  totalInterest: number;
  totalPenalties: number;
  totalRepayment: number;
}

interface InquiryState {
  step: "initial" | "otp" | "verify" | "repayment" | "payment";
  identifier: string;
  identifierType: "MOBILE" | "PAN";
  publicLoanInquiriesId: string;
  otp: string;
  selectedLoan: Loan | null;
  loans: Loan[];
  repaymentDetails: RepaymentDetails | null;
  error: string;
  loading: boolean;
  rateLimitRemaining: number;
  showBankDetails: boolean;
}

interface PanValidationHintProps {
  panValue: string;
}

const PanValidationHint: React.FC<PanValidationHintProps> = ({ panValue }) => {
  const validatePan = (pan: string) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (pan.length === 0) return null;
    if (panRegex.test(pan)) return { valid: true, message: "✓ Valid PAN format", color: "text-green-600" };
    const totalLength = pan.length;
    if (totalLength < 10) return { valid: false, message: `PAN should be 10 characters (you have ${totalLength})`, color: "text-amber-500" };
    if (totalLength > 10) return { valid: false, message: `PAN should be 10 characters (you have ${totalLength})`, color: "text-red-500" };
    return { valid: false, message: "Invalid PAN format (5 letters, 4 digits, 1 letter)", color: "text-red-500" };
  };
  const validation = validatePan(panValue);
  if (!validation) return null;
  return <p className={`text-xs mt-2 font-medium ${validation.color}`}>{validation.message}</p>;
};

const RepaymentNow: React.FC = () => {
  const [inquiry, setInquiry] = useState<InquiryState>({
    step: "initial",
    identifier: "",
    identifierType: "MOBILE",
    publicLoanInquiriesId: "",
    otp: "",
    selectedLoan: null,
    loans: [],
    repaymentDetails: null,
    error: "",
    loading: false,
    rateLimitRemaining: 5,
    showBankDetails: false,
  });
  const [copied, setCopied] = useState(false);

  // const getBrandLogo = () => "";

  const handleInitiateInquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inquiry.identifier) {
      setInquiry((prev) => ({ ...prev, error: "Please enter mobile or PAN" }));
      return;
    }
    setInquiry((prev) => ({ ...prev, loading: true, error: "" }));
    const identifier =
      inquiry.identifierType === "MOBILE"
        ? inquiry.identifier.startsWith("+91") ? inquiry.identifier : `+91${inquiry.identifier}`
        : inquiry.identifier;
    try {
      const response = await fetch(`${API_BASE_URL}/payment/public/loan-inquiry/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [inquiry.identifierType === "MOBILE" ? "mobile" : "pan"]: identifier }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to initiate inquiry");
      }
      const data = await response.json();
      setInquiry((prev) => ({ ...prev, step: "otp", publicLoanInquiriesId: data.data.publicLoanInquiriesId, rateLimitRemaining: data.data.rateLimit.remaining, loading: false }));
    } catch (err) {
      setInquiry((prev) => ({ ...prev, error: err instanceof Error ? err.message : "An error occurred", loading: false }));
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inquiry.otp || inquiry.otp.length !== 6) {
      setInquiry((prev) => ({ ...prev, error: "Please enter a valid 6-digit OTP" }));
      return;
    }
    setInquiry((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const response = await fetch(`${API_BASE_URL}/payment/public/loan-inquiry/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publicLoanInquiriesId: inquiry.publicLoanInquiriesId, otp: inquiry.otp, identifier: inquiry.identifier }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Invalid OTP");
      }
      const data = await response.json();
      setInquiry((prev) => ({ ...prev, step: "verify", loans: data.data.activeLoans || data.data.loans || [], loading: false }));
    } catch (err) {
      setInquiry((prev) => ({ ...prev, error: err instanceof Error ? err.message : "An error occurred", loading: false }));
    }
  };

  const handleSelectLoan = async (loan: Loan) => {
    setInquiry((prev) => ({ ...prev, loading: true, error: "", selectedLoan: loan }));
    try {
      const response = await fetch(`${API_BASE_URL}/payment/public/loan-inquiry/repayment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publicLoanInquiriesId: inquiry.publicLoanInquiriesId, otp: inquiry.otp, loanId: loan.id }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch repayment details");
      }
      const data = await response.json();
      setInquiry((prev) => ({ ...prev, step: "repayment", repaymentDetails: data.data, loading: false }));
    } catch (err) {
      setInquiry((prev) => ({ ...prev, error: err instanceof Error ? err.message : "An error occurred", loading: false }));
    }
  };

  const handleCreatePayment = async (method: "RAZORPAY" | "PAYTERNING" | "MANUAL" | "CASHFREE") => {
    if (method === "MANUAL") {
      setInquiry((prev) => ({ ...prev, showBankDetails: true }));
      return;
    }
    setInquiry((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const response = await fetch(`${API_BASE_URL}/payment/public/loan-inquiry/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publicLoanInquiriesId: inquiry.publicLoanInquiriesId, otp: inquiry.otp, loanId: inquiry.selectedLoan?.id, method }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create payment");
      }
      const data = await response.json();
      if (method === "PAYTERNING" && data.data.paymentLink) window.location.href = data.data.paymentLink;
      else if (method === "RAZORPAY" && data.data.paymentLink) window.location.href = data.data.paymentLink;
      else if (method === "CASHFREE" && data.data.paymentLink) window.location.href = data.data.cashfreeLink;
      else setInquiry((prev) => ({ ...prev, step: "payment", loading: false }));
    } catch (err) {
      setInquiry((prev) => ({ ...prev, error: err instanceof Error ? err.message : "An error occurred", loading: false }));
    }
  };

  const handleReset = () => {
    setInquiry({ step: "initial", identifier: "", identifierType: "MOBILE", publicLoanInquiriesId: "", otp: "", selectedLoan: null, loans: [], repaymentDetails: null, error: "", loading: false, rateLimitRemaining: 5, showBankDetails: false });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const stepLabels: Record<string, string> = {
    initial: "Check Your Loan",
    otp: "Verify Identity",
    verify: "Select Loan",
    repayment: "Repayment Summary",
    payment: "Payment Confirmed",
  };

  return (
    <div className="min-h-screen bg-white text-text-primary font-sans selection:bg-blue-100 selection:text-primary">
      <SEO
        title="Repay Now"
        description={`Official repayment portal for ${import.meta.env.VITE_COMPANY_NAME}. Access your loan records and initiate secure repayments.`}
        keywords={`loan repayment India, repay personal loan, ${import.meta.env.VITE_COMPANY_NAME} payment`}
      />

      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-blue-50/40 to-white" />
      <div className="relative z-20"><Navbar /></div>

      {/* Hero */}
      <section className="w-full pt-36 pb-16 text-center px-6 relative">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-white border border-blue-100 shadow-sm rounded-full mb-8">
            <div className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest text-text-secondary uppercase">Loan Repayment Portal</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6 text-text-primary">
            {stepLabels[inquiry.step].split(" ").slice(0, -1).join(" ")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              {stepLabels[inquiry.step].split(" ").slice(-1)[0]}.
            </span>
          </h1>
          <p className="text-text-secondary text-lg font-medium max-w-xl mx-auto">
            {inquiry.step === "initial" && "Enter your mobile number or PAN to access your active loan records."}
            {inquiry.step === "otp" && `We've sent a 6-digit OTP to ${inquiry.identifier}.`}
            {inquiry.step === "verify" && "We found active loans linked to your account. Select one to proceed."}
            {inquiry.step === "repayment" && "Review your repayment breakdown and choose a payment method."}
            {inquiry.step === "payment" && "Your repayment request has been submitted successfully."}
          </p>
        </div>
      </section>

      <div className="relative z-10 max-w-3xl mx-auto px-6 pb-24">

        {/* Error */}
        {inquiry.error && (
          <div className="mb-8 flex items-start gap-3 p-5 rounded-2xl bg-red-50 border border-red-100 text-red-600">
            <span className="text-lg mt-0.5">⚠️</span>
            <span className="text-sm font-medium">{inquiry.error}</span>
          </div>
        )}

        {/* STEP 1: Identification */}
        {inquiry.step === "initial" && (
          <form onSubmit={handleInitiateInquiry} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-12">
            <h2 className="text-xl font-bold text-text-primary mb-8">Enter Your Details</h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-3">Identifier Type</label>
                <div className="flex flex-col gap-3">
                  {["MOBILE", "PAN"].map((type) => (
                    <label
                      key={type}
                      className={`flex items-center justify-between px-5 py-4 rounded-2xl border-2 cursor-pointer transition-all ${inquiry.identifierType === type ? 'border-primary bg-blue-50 text-primary' : 'border-gray-100 bg-white hover:border-primary/30'}`}
                    >
                      <span className="font-semibold text-sm">{type === "MOBILE" ? "📱 Mobile Number" : "🪪 PAN Card"}</span>
                      <input
                        type="radio"
                        name="idType"
                        className="hidden"
                        checked={inquiry.identifierType === type}
                        onChange={() => setInquiry(prev => ({ ...prev, identifierType: type as any, identifier: "" }))}
                      />
                      {inquiry.identifierType === type && <FaCheckCircle className="text-primary" />}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-3">
                  {inquiry.identifierType === "MOBILE" ? "Mobile Number" : "PAN Number"}
                </label>
                <input
                  type="text"
                  placeholder={inquiry.identifierType === "MOBILE" ? "10-digit mobile number" : "e.g. ABCDE1234F"}
                  value={inquiry.identifier.replace(/^\+91/, "")}
                  onChange={(e) => {
                    let value = e.target.value.trim();
                    if (inquiry.identifierType === "MOBILE") {
                      value = value.replace(/\D/g, "").slice(0, 10);
                      value = value ? `+91${value}` : "";
                    } else {
                      value = value.toUpperCase();
                    }
                    setInquiry((prev) => ({ ...prev, identifier: value }));
                  }}
                  className="w-full px-5 py-4 rounded-2xl border border-gray-200 bg-white text-text-primary font-bold text-xl focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all placeholder:text-gray-300"
                />
                <p className="text-xs text-text-secondary mt-2 font-medium">Required: {inquiry.identifierType === "MOBILE" ? "10 digits" : "10 characters"}</p>
                {inquiry.identifierType === "PAN" && inquiry.identifier && (
                  <PanValidationHint panValue={inquiry.identifier} />
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={inquiry.loading}
              className="w-full py-5 bg-primary text-white font-bold text-base rounded-2xl shadow-md hover:bg-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inquiry.loading ? "Verifying..." : "Check My Loan →"}
            </button>
          </form>
        )}

        {/* STEP 2: OTP Verification */}
        {inquiry.step === "otp" && (
          <form onSubmit={handleVerifyOTP} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-12 max-w-lg mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4">🔐</div>
              <h2 className="text-xl font-bold text-text-primary mb-2">Enter OTP</h2>
              <p className="text-sm text-text-secondary font-medium">Sent to <span className="font-bold text-text-primary">{inquiry.identifier}</span></p>
            </div>

            <input
              type="text"
              maxLength={6}
              placeholder="000000"
              value={inquiry.otp}
              onChange={(e) => setInquiry((prev) => ({ ...prev, otp: e.target.value }))}
              className="w-full px-6 py-5 rounded-2xl border border-gray-200 text-5xl font-bold tracking-[0.5em] text-center text-text-primary focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all placeholder:text-gray-200 mb-4"
            />

            <div className="flex justify-between text-xs font-semibold text-text-secondary mb-8">
              <span>Expires in 120 seconds</span>
              <span>Attempts left: {inquiry.rateLimitRemaining}/5</span>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={handleReset} className="h-13 px-8 rounded-2xl border-2 border-gray-200 text-text-primary font-semibold hover:bg-gray-50 transition-all flex items-center gap-2">
                <FaArrowLeft size={12} /> Back
              </button>
              <button
                type="submit"
                disabled={inquiry.loading}
                className="flex-1 py-4 bg-primary text-white font-bold rounded-2xl shadow-md hover:bg-secondary transition-all disabled:opacity-50"
              >
                {inquiry.loading ? "Verifying..." : "Verify OTP →"}
              </button>
            </div>
          </form>
        )}

        {/* STEP 3: Loan Selector */}
        {inquiry.step === "verify" && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-12">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-bold text-text-primary">Your Active Loans</h2>
              <span className="text-xs font-bold text-text-secondary bg-blue-50 px-3 py-1 rounded-full border border-blue-100">{inquiry.loans.length} Found</span>
            </div>

            <div className="flex flex-col gap-4">
              {inquiry.loans.map((loan) => (
                <button
                  key={loan.id}
                  onClick={() => handleSelectLoan(loan)}
                  disabled={inquiry.loading}
                  className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6 rounded-2xl border border-gray-100 hover:border-primary hover:bg-blue-50/40 transition-all group text-left"
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-bold text-text-secondary uppercase tracking-widest">Loan ID</span>
                    <span className="font-bold text-lg text-text-primary group-hover:text-primary transition-colors">{loan.formattedLoanId}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-bold text-text-secondary uppercase tracking-widest">Amount</span>
                    <span className="font-extrabold text-2xl text-text-primary">₹{loan.amount.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-bold text-text-secondary uppercase tracking-widest">Status</span>
                    <span className="font-semibold text-sm px-3 py-1 rounded-full bg-green-100 text-green-700 w-fit">{loan.status}</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all shrink-0">›</div>
                </button>
              ))}
            </div>

            <button onClick={handleReset} className="mt-8 text-sm font-semibold text-text-secondary hover:text-primary transition-colors flex items-center gap-2">
              <FaArrowLeft size={11} /> Start Over
            </button>
          </div>
        )}

        {/* STEP 4: Repayment Summary */}
        {inquiry.step === "repayment" && (
          <div className="flex flex-col gap-6">

            {/* Loan Summary Card */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-10">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <p className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Selected Loan</p>
                  <h3 className="text-2xl font-extrabold text-text-primary">{inquiry.selectedLoan?.formattedLoanId}</h3>
                </div>
              </div>

              {inquiry.repaymentDetails && (
                <div className="space-y-3 mb-8">
                  {[
                    { label: "Principal Amount", val: inquiry.repaymentDetails.principalAmount },
                    { label: "Interest Accrued", val: inquiry.repaymentDetails.totalInterest },
                    { label: "Penalties Applied", val: inquiry.repaymentDetails.totalPenalties },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between items-center py-4 border-b border-gray-100">
                      <span className="text-sm font-medium text-text-secondary">{row.label}</span>
                      <span className="font-bold text-lg text-text-primary">₹{row.val?.toLocaleString("en-IN") || 0}</span>
                    </div>
                  ))}

                  {/* Total */}
                  <div className="flex justify-between items-center py-5 px-6 rounded-2xl bg-primary text-white mt-4">
                    <span className="font-bold">Total Due</span>
                    <span className="font-extrabold text-3xl">₹{inquiry.repaymentDetails.totalRepayment?.toLocaleString("en-IN") || 0}</span>
                  </div>
                </div>
              )}

              {/* Payment Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleCreatePayment(import.meta.env.VITE_PAYMENT_GATEWAY_METHOD)}
                  disabled={inquiry.loading}
                  className="w-full py-5 bg-primary text-white font-bold rounded-2xl shadow-md hover:bg-secondary transition-all disabled:opacity-50"
                >
                  {inquiry.loading ? "Processing..." : `Pay Now via ${import.meta.env.VITE_PAYMENT_GATEWAY_METHOD || "Gateway"} →`}
                </button>
                {!inquiry.showBankDetails && (
                  <button
                    onClick={() => setInquiry(prev => ({ ...prev, showBankDetails: true }))}
                    className="w-full py-4 border-2 border-gray-200 text-text-primary font-bold rounded-2xl hover:bg-gray-50 transition-all"
                  >
                    Pay via Bank Transfer
                  </button>
                )}
              </div>
            </div>

            {/* Bank Details */}
            {inquiry.showBankDetails && (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-primary">🏦</div>
                  <h3 className="text-lg font-bold text-text-primary">Bank Transfer Details</h3>
                </div>

                {BANK_DETAILS.upiId && (
                  <div className="flex items-center justify-between p-4 rounded-2xl bg-blue-50/60 border border-blue-100 mb-6">
                    <div>
                      <p className="text-xs font-bold text-text-secondary uppercase tracking-widest mb-1">UPI ID</p>
                      <p className="font-bold text-text-primary">{BANK_DETAILS.upiId}</p>
                    </div>
                    <button onClick={() => copyToClipboard(BANK_DETAILS.upiId)} className="w-10 h-10 rounded-xl bg-white border border-blue-100 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all">
                      <FaRegCopy size={14} />
                    </button>
                  </div>
                )}

                <div className="space-y-3">
                  {[
                    { label: "Bank Name", val: BANK_DETAILS.bankName },
                    { label: "Account Number", val: BANK_DETAILS.accountNo },
                    { label: "IFSC Code", val: BANK_DETAILS.ifsc },
                    { label: "Account Type", val: BANK_DETAILS.accountType },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-sm font-medium text-text-secondary">{item.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-text-primary">{item.val || "—"}</span>
                        {item.val && (
                          <button onClick={() => copyToClipboard(item.val)} className="text-text-secondary hover:text-primary transition-colors">
                            <FaRegCopy size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {copied && (
                  <div className="mt-4 p-3 rounded-2xl bg-green-50 border border-green-100 text-green-600 text-sm font-semibold text-center flex items-center justify-center gap-2">
                    <FaCheckCircle /> Copied to clipboard!
                  </div>
                )}

                <button onClick={handleReset} className="mt-6 text-sm font-semibold text-text-secondary hover:text-primary transition-colors flex items-center gap-2">
                  <FaArrowLeft size={11} /> Start Over
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 5: Payment Confirmed */}
        {inquiry.step === "payment" && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 md:p-16 text-center">
            <div className="w-20 h-20 bg-green-50 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-8">✅</div>
            <h3 className="text-3xl font-extrabold text-text-primary mb-4">Payment Submitted!</h3>
            <p className="text-text-secondary text-lg font-medium mb-10 max-w-md mx-auto leading-relaxed">
              Your repayment request has been successfully submitted and is being processed by our team.
            </p>
            <button
              onClick={handleReset}
              className="px-12 py-4 bg-primary text-white font-bold rounded-2xl shadow-md hover:bg-secondary transition-all"
            >
              Back to Home
            </button>
          </div>
        )}

      </div>

      <Footer />
    </div>
  );
};

export default RepaymentNow;
